import type { FC } from 'react';
import { memo } from 'react';

import { useTranslation } from 'react-i18next';

import { createAction } from '@pages/hubs/Garage/utils/createAction';
import { GalleryIcon, TrashIcon } from '@setup/svg-icons/svg-icons';
import { AlertModalVariantEnum } from '@shared/configs/modals/alertModal/alertModal.config';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import modalObserver from '@shared/utils/observers/modalObserver';

import { AttachmentActions } from '../AttachmentActions/AttachmentActions';
import { Caption } from '../Caption/Caption';
import { Image } from '../Image/Image';
import { UploadProgress } from '../UploadProgress/UploadProgress';

import { AttachmentsItemCardContentContainer, AttachmentsItemWrapper } from './AttachmentsItemCard.style';

import type { SxProps, Theme } from '@mui/material';
import type { MediaType } from '@shared/models/media.model';

export type AttachmentsItemCardProps = {
  attachmentsItem: MediaType;
  fileNameLabelId?: string;
  handleOpenViewModal: (activeIndex: number) => void;
  onDeleteAttachment: (id: number, token: string) => void;
  isCanDelete?: boolean;
  index: number;
  heightImage?: number;
  isShowCoverPhoto?: boolean;
  isShowActionMenu?: boolean;
  handleChangeCoverPhoto?: (newCoverID: number) => void;
  sxAttachContainer?: SxProps<Theme>;
  showDeletePopupOnDelete?: true;
  attachmentType?: 'photo' | 'file';
  showItemCaption?: boolean;
  aspectRatio?: string | number;
  isProtectedUrl?: boolean;
};

export const AttachmentsItemCard: FC<AttachmentsItemCardProps> = memo(
  ({
    attachmentsItem,
    index,
    fileNameLabelId,
    handleOpenViewModal,
    isCanDelete,
    onDeleteAttachment,
    heightImage,
    isShowCoverPhoto = false,
    isShowActionMenu = false,
    handleChangeCoverPhoto,
    sxAttachContainer,
    showDeletePopupOnDelete,
    attachmentType = 'photo',
    showItemCaption = true,
    aspectRatio = '4/3',
    isProtectedUrl = false,
  }) => {
    const isLoading = attachmentsItem.progress !== undefined;

    const { t } = useTranslation();

    const makeCover = createAction('Make cover', GalleryIcon, () => {
      const newCoverID = attachmentsItem.id;

      handleChangeCoverPhoto?.(newCoverID);
    });
    const del = createAction('Delete', TrashIcon, () => {
      const isFileType = attachmentType === 'file';

      const onDeleteHandler = () => {
        onDeleteAttachment(attachmentsItem.id, attachmentsItem.token);
      };
      if (showDeletePopupOnDelete) {
        modalObserver.addModal(ModalNamesEnum.alertModal, {
          props: {
            variant: AlertModalVariantEnum.delete,
            entityName: t(`general.${isFileType ? 'file' : 'photo'}`),
            onClickRightBtn: onDeleteHandler,
          },
        });
      } else {
        onDeleteHandler();
      }
    });

    const actionMenu = attachmentsItem.is_cover ? [del] : [makeCover, del];

    const deleteAttachmentHandler = () => {
      if (showDeletePopupOnDelete) {
        del.callback();
      } else {
        onDeleteAttachment(attachmentsItem.id, attachmentsItem.token);
      }
    };

    return (
      <AttachmentsItemWrapper
        isLoading={isLoading}
        onClick={() => {
          return isLoading ? true : handleOpenViewModal(index);
        }}
      >
        {isLoading ? (
          <UploadProgress
            attachmentsItem={attachmentsItem}
            heightImage={heightImage}
            aspectRatio={aspectRatio}
          />
        ) : (
          <AttachmentsItemCardContentContainer sx={{ ...sxAttachContainer }}>
            <Image
              attachmentsItem={attachmentsItem}
              heightImage={heightImage}
              aspectRatio={aspectRatio}
              isProtectedUrl={isProtectedUrl}
            />
            <AttachmentActions
              attachmentsItem={attachmentsItem}
              deleteAttachmentHandler={deleteAttachmentHandler}
              actionMenu={actionMenu}
              isShowActionMenu={isShowActionMenu}
              isCanDelete={isCanDelete}
              isShowCoverPhoto={isShowCoverPhoto}
            />
          </AttachmentsItemCardContentContainer>
        )}

        {showItemCaption && <Caption id={fileNameLabelId} attachmentsItem={attachmentsItem} />}
      </AttachmentsItemWrapper>
    );
  },
);
